// Console shell v3 route registry (product lock 2026-08-15, design board Implement rule 8).
//
// Every top-level route declares which of the three locked templates it is (Help is the
// workbench's document variant) and how the shell dresses <main> for it. A new route that
// fits none of the three templates is a design review, not a fourth template.

export type ShellTemplate = "map-first" | "master-detail" | "workbench" | "document";

/**
 * How the shell dresses `.appshell__content` for a route.
 *
 * - `"cad"`: the map-first desk. Zero padding, no scrolling; the map owns the stage and
 *   chrome floats (2026-08-10 CAD lock, unchanged by shell v3).
 * - `"stage"`: shell v3 chrome for migrated routes. Zero padding, overflow hidden; the
 *   template primitive (WorkbenchLayout / MasterDetailLayout / DocumentLayout) owns
 *   scrolling, so long lists scroll inside panes instead of the whole page.
 * - `"scroll"`: the legacy padded page scroller. Every route starts here and its migration
 *   PR (terminals -2 / -3) flips it to "stage" in the same change that adopts the template
 *   primitive. Flipping without migrating strands the old page in an unscrollable box.
 */
export type StageChrome = "cad" | "stage" | "scroll";

export interface RouteShellSpec {
  /** Matches the NAV_ITEMS key for the same route. */
  key: string;
  path: string;
  template: ShellTemplate;
  /** Whether the migrated route mounts a ShellSubrail (board route registry). */
  subrail: boolean;
  stage: StageChrome;
}

export const ROUTE_SHELL: readonly RouteShellSpec[] = [
  { key: "inbox", path: "/inbox", template: "master-detail", subrail: false, stage: "stage" },
  { key: "command", path: "/", template: "map-first", subrail: false, stage: "cad" },
  // Wave 1: workbenches (#329) and master-detail/document (#330) both ride stage chrome.
  { key: "patrols", path: "/patrols", template: "workbench", subrail: true, stage: "stage" },
  { key: "schedule", path: "/schedule", template: "workbench", subrail: true, stage: "stage" },
  { key: "setup", path: "/setup", template: "workbench", subrail: true, stage: "stage" },
  { key: "incidents", path: "/incidents", template: "master-detail", subrail: false, stage: "stage" },
  { key: "reports", path: "/reports", template: "master-detail", subrail: false, stage: "stage" },
  { key: "admin", path: "/admin", template: "workbench", subrail: true, stage: "stage" },
  { key: "platform", path: "/platform", template: "workbench", subrail: true, stage: "stage" },
  { key: "help", path: "/help", template: "document", subrail: true, stage: "stage" },
];

/** The registry entry owning `pathname` (exact, then longest-prefix; "/" only exact). */
export function routeShellForPath(pathname: string): RouteShellSpec | null {
  const normalized = pathname === "" ? "/" : pathname;
  const exact = ROUTE_SHELL.find((route) => route.path === normalized);
  if (exact !== undefined) {
    return exact;
  }
  return (
    ROUTE_SHELL.find((route) => route.path !== "/" && normalized.startsWith(`${route.path}/`)) ??
    null
  );
}

/** Stage chrome for `pathname`; unknown paths get the legacy scroller. */
export function stageChromeForPath(pathname: string): StageChrome {
  return routeShellForPath(pathname)?.stage ?? "scroll";
}
